import { _ as _async_to_generator } from "@swc/helpers/_/_async_to_generator";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _ts_generator } from "@swc/helpers/_/_ts_generator";
import { useAddedToBagContextStore } from "@faire/retailer-visitor-shared/components/Notifications/AddedToBag/AddedToBagStore";
import { useInvalidateCart } from "@faire/retailer-visitor-shared/features/Cart/useCart";
import { useCartItems, useInvalidateCartItems } from "@faire/retailer-visitor-shared/hooks/cart/useCartItems";
import { useInvalidateCartSummary } from "@faire/retailer-visitor-shared/hooks/cart/useCartSummary";
import { useIsOnCartPage } from "@faire/retailer-visitor-shared/hooks/cart/useIsOnCartPage";
import { commonThrottle } from "@faire/retailer-visitor-shared/lib/commonThrottle";
import { generateBrandCartForUpdates } from "@faire/retailer-visitor-shared/lib/updateCartV2";
import { getCartToken } from "@faire/retailer-visitor-shared/serialized-data/getCartToken";
import { triggerAddToCartEventV2 } from "@faire/retailer-visitor-shared/stores/domain/CartStore/triggerAddToCartEvent";
import { useInvalidateBrandCart } from "@faire/retailer-visitor-shared/views/brand/BrandCart/hooks/useBrandCart";
import { useMutation as useCartUpdateMutation } from "@faire/web-api--source/api/v2/carts/cartToken/put-hooks";
import { IUpdateCartRequestV2 } from "@faire/web-api--source/faire/carts/IUpdateCartRequestV2";
import cloneDeep from "lodash/cloneDeep";
import isEmpty from "lodash/isEmpty";
import { useCallback, useMemo } from "react";
export var useCartUpdate = function() {
    var leanCartItemMap = useCartItems().leanCartItemMap;
    var _useInvalidateCartItems = useInvalidateCartItems(), invalidateCartItemInventory = _useInvalidateCartItems.invalidateCartItemInventory, invalidateLeanBrandsMap = _useInvalidateCartItems.invalidateLeanBrandsMap;
    var invalidateBrandCart = useInvalidateBrandCart().invalidateBrandCart;
    var invalidateEntireCartSummary = useInvalidateCartSummary().invalidateEntireCartSummary;
    var invalidateEntireCart = useInvalidateCart().invalidateEntireCart;
    var isOnCartPage = useIsOnCartPage();
    var _useAddedToBagContextStore = _sliced_to_array(useAddedToBagContextStore([]), 2), setAddedToBagStore = _useAddedToBagContextStore[1];
    var _useCartUpdateMutation = useCartUpdateMutation(), updateCartMutation = _useCartUpdateMutation.mutateAsync, mutationResult = _object_without_properties(_useCartUpdateMutation, [
        "mutateAsync"
    ]);
    var handleUpdateCartSuccess = useCallback(function(updateResponse, upltParams, skipCartRefetch, request) {
        triggerAddToCartEventV2(updateResponse, cloneDeep(leanCartItemMap !== null && leanCartItemMap !== void 0 ? leanCartItemMap : {}), upltParams);
        invalidateCartItemInventory();
        invalidateLeanBrandsMap();
        invalidateEntireCartSummary();
        if (!isEmpty(updateResponse.updated_brand_data)) {
            updateResponse.updated_brand_data.forEach(function(brandData) {
                var _brandData_brand_token;
                invalidateBrandCart({
                    brandToken: (_brandData_brand_token = brandData.brand_token) !== null && _brandData_brand_token !== void 0 ? _brandData_brand_token : ""
                });
            });
        } else if (request && !isEmpty(request.brand_carts)) {
            request.brand_carts.forEach(function(brandCart) {
                var _brandCart_brand_token;
                invalidateBrandCart({
                    brandToken: (_brandCart_brand_token = brandCart.brand_token) !== null && _brandCart_brand_token !== void 0 ? _brandCart_brand_token : ""
                });
            });
        }
        if (!skipCartRefetch) {
            // If we add to cart from other pages, we still need to invalidate the cart page cart
            invalidateEntireCart();
        }
        if (!isOnCartPage) {
            // We need to clear the Router cache so that the RSC payload is refetched
            setAddedToBagStore({
                shouldRefreshCartPage: true
            });
        }
    }, [
        leanCartItemMap,
        invalidateCartItemInventory,
        invalidateLeanBrandsMap,
        invalidateEntireCartSummary,
        invalidateBrandCart,
        invalidateEntireCart,
        isOnCartPage,
        setAddedToBagStore
    ]);
    var updateCartItems = useCallback(/*#__PURE__*/ function() {
        var _ref = _async_to_generator(function(param) {
            var items, brand_carts, requested_ship_dates, allowEmptyItems, skipCartRefetch, upltParams, request;
            return _ts_generator(this, function(_state) {
                items = param.items, brand_carts = param.brand_carts, requested_ship_dates = param.requested_ship_dates, allowEmptyItems = param.allowEmptyItems, skipCartRefetch = param.skipCartRefetch, upltParams = param.upltParams;
                if (!allowEmptyItems && !(items === null || items === void 0 ? void 0 : items.length)) {
                    return [
                        2
                    ];
                }
                request = IUpdateCartRequestV2.build({
                    brand_carts: generateBrandCartForUpdates(brand_carts !== null && brand_carts !== void 0 ? brand_carts : [], items, requested_ship_dates)
                });
                // TODO: If experiment ships, consider moving the trackTiming in QuickAdd here and use regular mutate instead
                return [
                    2,
                    updateCartMutation([
                        getCartToken(),
                        request
                    ], {
                        onSuccess: function(updateResponse) {
                            handleUpdateCartSuccess(updateResponse, upltParams, skipCartRefetch, request);
                        }
                    })
                ];
            });
        });
        return function(_) {
            return _ref.apply(this, arguments);
        };
    }(), [
        handleUpdateCartSuccess,
        updateCartMutation
    ]);
    var throttledUpdateCartItems = useMemo(function() {
        return commonThrottle(updateCartItems);
    }, [
        updateCartItems
    ]);
    // TODO: This is a temporary wrapper for updateCartItems to fit in UpdateCartHandler,
    // we should be able to delete this when we fully migrate to hooks
    var updateCartItemsWithUpdateHandler = /*#__PURE__*/ function() {
        var _ref = _async_to_generator(function(items, options) {
            var upltParams;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        upltParams = options.upltParams;
                        return [
                            4,
                            throttledUpdateCartItems({
                                items: items,
                                upltParams: upltParams
                            })
                        ];
                    case 1:
                        _state.sent();
                        return [
                            2
                        ];
                }
            });
        });
        return function updateCartItemsWithUpdateHandler(items, options) {
            return _ref.apply(this, arguments);
        };
    }();
    return _object_spread_props(_object_spread({}, mutationResult), {
        updateCartItems: throttledUpdateCartItems,
        updateCartItemsWithUpdateHandler: updateCartItemsWithUpdateHandler,
        handleUpdateCartSuccess: handleUpdateCartSuccess
    });
};
